import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useActiveAccount } from 'thirdweb/react'
import { LOCAL_STORAGE } from 'app/localStorageManager'
import { useRootContext } from 'app/services/RootProvider/RootProvider'
import { useWalletContext } from 'app/services/WalletProvider/WalletProvider'
import tagManager from 'app/tagManager'
import { ERROR_CODE } from 'app/util/errorCodes'
import { useModalContext } from 'components/interface/Modal/provider/ModalProvider'
import useDelayedActiveState from 'lib/react/hooks/useDelayedActiveState'
import useLogger from 'lib/util/useLogger'
import clearLocalStorageCache from './actions/clearLocalStorageCache'
import getAccount from './actions/getAccount'
import submitSignUpAccessCode from './actions/submitSignUpAccessCode'
import submitSignUpApply from './actions/submitSignUpApply'
import submitSignUpNewsletter from './actions/submitSignUpNewsletter'
import submitSignUpStandardUser from './actions/submitSignUpStandardUser'
import submitSignUpWithPassport from './actions/submitSignUpWithPassport'

// Logger
const debug = useLogger('Account', 'green')

/**
 * The `useAccount` hook
 *
 * @returns {object} the context object
 */
function useAccount () {
  const { appActivityStatus, setAppActivityLabel, setAppActivityStatus } =
    useRootContext()
  const { showModal } = useModalContext()
  const { connect, disconnect, getSigner, refreshThirdwebAccount, wallet } =
    useWalletContext()
  const [isUserActivityReady, setIsUserActivityReady] = useState(false)
  const delayedIsUserActivityReady = useDelayedActiveState(
    isUserActivityReady,
    0.2,
    0
  )
  const [account, setAccount] = useState({
    isSignedIn: false,
    isPassportHolder: false,
    hasAccountLoginError: false,
    hasWallet: false,
    wallet: null,
    user: null
  })
  const currentAppActivityStatus = useRef()
  const isInit = useRef(true)
  const walletAddress = useMemo(() => wallet?.address, [wallet])
  const accountInfo = useActiveAccount()

  /**
   * Update the account status
   */
  const refreshAccountStatus = useCallback(async () => {
    setAppActivityStatus('BUSY')
    debug.log('Getting account')

    const accountInfo = await getAccount(
      wallet?.address,
      getSigner,
      setAppActivityLabel
    )

    debug.log('Got account', accountInfo)

    const res = {
      isSignedIn: !!accountInfo?.isSignedIn,
      isPassportHolder: !!accountInfo?.isPassportHolder,
      hasAccountLoginError: !!accountInfo?.hasAccountLoginError,
      accountLoginErrorMessage: accountInfo?.accountLoginErrorMessage,
      hasWallet: !!wallet,
      wallet,
      user: accountInfo?.user || null
    }

    if (accountInfo?.isSignedIn) {
      // Store the signed in timestamp
      localStorage.setItem(LOCAL_STORAGE.lastSignIn, Date.now())

      tagManager.trackEvent({
        event: 'account.user-signed-in-session-begin'
      })
    }

    debug.log(res)
    setAccount(res)
    setAppActivityStatus(null)
    setAppActivityLabel(null)
  }, [wallet])

  /**
   * Logout the user
   */
  const logoutUser = useCallback(async () => {
    setAppActivityStatus('BUSY')
    disconnect()
    setAppActivityStatus(null)
    clearLocalStorageCache() // Clear account-related local storage

    showModal({
      id: 'app:signed_out'
    })

    tagManager.trackEvent({
      event: 'account.user-signed-out'
    })
  }, [account])

  /**
   * Login user
   */
  const loginUser = useCallback(async targetProvider => {
    if (currentAppActivityStatus?.current) {
      // Defensive when busy
      return
    }

    setAppActivityStatus('BUSY')
    await connect(targetProvider)

    setAppActivityStatus(null)
  }, [])

  /**
   * Sign up newsletter
   */
  const signUpNewsletter = useCallback(
    async formData => {
      const res = await submitSignUpNewsletter({
        ...formData,
        wallet: wallet?.address || ''
      })

      return res
    },
    [wallet]
  )

  /**
   * Sign up as a passport holder
   */
  const signUpPassportHolder = useCallback(
    async formData => {
      if (currentAppActivityStatus.current) {
        // Defensive when busy
        return {
          success: false,
          message: ERROR_CODE.BUSY
        }
      }

      setAppActivityStatus('BUSY')

      const { email, firstName, lastName, marketing } = formData
      const res = await submitSignUpWithPassport(
        {
          email,
          firstName,
          lastName,
          wallet: wallet?.address,
          marketing: !!marketing
        },
        getSigner
      )

      setAppActivityStatus(null)

      if (res.success) {
        // Success
        await refreshAccountStatus()
      }

      return res
    },
    [wallet]
  )

  /**
   * Sign up as a standard user (no passport)
   */
  const signUpStandardUser = useCallback(
    async formData => {
      if (currentAppActivityStatus.current) {
        return {
          success: false,
          message: ERROR_CODE.BUSY
        }
      }

      setAppActivityStatus('BUSY')

      const { email, firstName, lastName, marketing, referralCode } = formData
      const res = await submitSignUpStandardUser(
        {
          email,
          firstName,
          lastName,
          referralCode,
          wallet: wallet?.address,
          marketing: !!marketing
        },
        getSigner
      )

      setAppActivityStatus(null)

      if (res.success) {
        // Success
        await refreshAccountStatus()
      }

      return res
    },
    [wallet]
  )

  /**
   * Sign up with an access code - user has already
   * registered and is completing their sign up with
   * an access code
   */
  const signUpAccessCode = useCallback(
    async accessCode => {
      if (currentAppActivityStatus.current) {
        // Defensive when busy
        return {
          success: false,
          message: ERROR_CODE.BUSY
        }
      }

      setAppActivityStatus('BUSY')

      const res = await submitSignUpAccessCode(
        {
          access_code: accessCode,
          wallet: wallet.address
        },
        getSigner
      )

      setAppActivityStatus(null)

      if (res.success) {
        // Success
        await refreshAccountStatus()
      }

      return res
    },
    [wallet]
  )

  /**
   * Sign up using the apply method - which is the
   * intake form that is used for users registering
   * their interest in membership
   */
  const signUpApply = useCallback(async formData => {
    if (currentAppActivityStatus.current) {
      // Defensive when busy
      return {
        success: false,
        message: ERROR_CODE.BUSY
      }
    }

    setAppActivityStatus('BUSY')

    const res = await submitSignUpApply(formData)

    setAppActivityStatus(null)

    return res
  }, [])

  /**
   * On initialization, attempt to load the user
   */
  const loadUserOnInit = useCallback(async () => {
    setIsUserActivityReady(false)
    await refreshThirdwebAccount()
    setIsUserActivityReady(true)
  }, [accountInfo])

  const loadAccountStatusOnWallet = useCallback(async () => {
    setIsUserActivityReady(false)
    await refreshAccountStatus()
    setIsUserActivityReady(true)
  }, [walletAddress])

  /**
   * When the `appActivityStatus` changes, update
   * the ref
   */
  useEffect(() => {
    currentAppActivityStatus.current = appActivityStatus
  }, [appActivityStatus])

  /**
   * Effect: On mount
   */
  useEffect(() => {
    loadUserOnInit()
  }, [accountInfo])

  /**
   * Effect: When the wallet address changes,
   * test whether need to clear caches
   */
  useEffect(() => {
    if (!walletAddress) {
      return
    }

    const lastWallet = localStorage.getItem(LOCAL_STORAGE.lastWalletCache)

    if (wallet?.address) {
      if (lastWallet !== wallet.address) {
        clearLocalStorageCache() // Clear account-related local storage
      }

      localStorage.setItem(LOCAL_STORAGE.lastWalletCache, wallet.address)
    }
  }, [walletAddress])

  /**
   * When wallet info changes, refresh the account status
   */
  useEffect(() => {
    if (isInit.current && !walletAddress) {
      // When this is first run and there's no wallet
      // avoid refreshing the account status, however,
      // we do want to watch for future changes to the `walletAddress`
      // as that would mean the user could have disconnected their
      // wallet, in which case we do want to refresh the account status
      isInit.current = false

      return
    }

    isInit.current = false

    if (!walletAddress) {
      refreshAccountStatus()

      return
    }

    loadAccountStatusOnWallet()
  }, [walletAddress])

  /**
   * Memo: The context
   */
  const ctx = useMemo(
    () => ({
      account,
      isUserActivityReady: delayedIsUserActivityReady,
      refreshAccountStatus,
      loginUser,
      logoutUser,
      signUpNewsletter,
      signUpApply,
      signUpPassportHolder,
      signUpStandardUser,
      signUpAccessCode
    }),
    [
      account,
      delayedIsUserActivityReady,
      refreshAccountStatus,
      loginUser,
      logoutUser,
      signUpNewsletter,
      signUpApply,
      signUpPassportHolder,
      signUpStandardUser,
      signUpAccessCode
    ]
  )

  return ctx
}

// Export
export default useAccount
